import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import Band, { Eyebrow } from "@/components/site/Band";
import { HOW_STEPS } from "@/lib/content";

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How it works — a real timeline in weeks"
        description="A real timeline in weeks, not the word fast. What we need from your practice, what Health and More handles, and when first revenue realistically appears."
        path="/how-it-works"
      />

      <Band tone="spruce">
        <Eyebrow>How it works</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mb-6 max-w-3xl">
          A real timeline, in weeks. Not the word "fast."
        </h1>
        <p className="text-bone/85 text-base sm:text-lg max-w-2xl">
          Every competitor over-promises the timeline. We under-promise it, because a program that starts
          late but runs clean beats one that was sold as instant. Here is what actually happens.
        </p>
      </Band>

      <Band tone="bone">
        <div className="space-y-0">
          {HOW_STEPS.map((s, i) => (
            <div
              key={i}
              className="grid lg:grid-cols-[10rem_1fr] gap-4 lg:gap-10 py-8 border-t border-sage/40 first:border-t-0"
              data-testid={`timeline-${i}`}
            >
              <div className="tabular text-brass text-lg font-medium">{s.week}</div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl text-spruce mb-4">{s.title}</h2>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <div className="tabular text-xs uppercase tracking-wider text-sage mb-1.5">From your practice</div>
                    <p className="text-sm text-ink/80 leading-relaxed">{s.practice}</p>
                  </div>
                  <div>
                    <div className="tabular text-xs uppercase tracking-wider text-sage mb-1.5">Health and More handles</div>
                    <p className="text-sm text-ink/80 leading-relaxed">{s.us}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Band>

      <Band tone="ink">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-4">
            First revenue, stated honestly.
          </h2>
          <p className="text-sage text-base mb-8">
            Most programs show meaningful numbers by month three to four — after credentialing clears and
            the first full billing cycles run. Anyone who promises revenue in week one is selling you the
            timeline, not the program.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-sm font-semibold text-spruce bg-brass px-6 py-3 rounded-[2px] hover:brightness-95 transition-all"
            data-testid="how-cta"
          >
            Start the conversation <ArrowRight size={16} />
          </Link>
        </div>
      </Band>
    </>
  );
}
